import { Trash, ChevronsUpDown, Copy, Check, Save } from "lucide-react";
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setLastEditedDocumentField, clearLastEditedDocumentField } from '../../../store/reducers/index';
import { DocumentosModal, getDocumentos } from './DocumentosModal';
import { DocumentosTipoModal, getDocumentosTipos } from './DocumentosModal';
import { getDeclarationSummaryTranslation } from '../../../hooks/useLanguage';

// Reusable Input with Copy Button Component
const InputWithCopy = ({ id, value, placeholder, onChange, className, type = "text", required = false, maxLength }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (value === null || value === undefined || value === '') return;
    
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Check if value exists (including 0 as a valid value)
  const hasValue = value !== null && value !== undefined && value !== '';

  // Handle value properly - don't convert 0 to empty string
  const inputValue = value === null || value === undefined ? "" : value;

  return (
    <div className="relative">
      <input
        id={id}
        type={type}
        value={inputValue}
        placeholder={placeholder}
        onChange={onChange}
        className={className}
        required={required}
        maxLength={maxLength}
      />
      {hasValue && (
        <button
          type="button"
          onClick={handleCopy}
          className="absolute right-0 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded transition-colors"
          title={copied ? "Copied!" : "Copy"}
        >
          {copied ? (
            <Check className="w-4 h-4 text-green-500" />
          ) : (
            <Copy className="w-4 h-4 text-gray-500" />
          )}
        </button>
      )}
    </div>
  );
};

export const DeclarationDocumentsTableContent = ({
  declarationIndex,
  isSpanish,
  declaration,
  editableData,
  setEditableData,
  setIsDirty,
  openDocModalIndex,
  setOpenDocModalIndex,
  openTipoModal,
  setOpenTipoModal,
}) => {
  const dispatch = useDispatch();
  const documentos = getDocumentos();
  const lastEditedDocumentField = useSelector(state => state.documents.lastEditedDocumentField);
  const lastEditedDocumentDeclarationIndex = useSelector(state => state.documents.lastEditedDocumentDeclarationIndex);
  const lastEditedDocumentPartidaIndex = useSelector(state => state.documents.lastEditedDocumentPartidaIndex);
  const lastEditedDocumentDocumentIndex = useSelector(state => state.documents.lastEditedDocumentDocumentIndex);
  
  // Check if we can show "Apply to All" button
  const canShowApplyButton =
    !!lastEditedDocumentField &&
    lastEditedDocumentDeclarationIndex === declarationIndex &&
    lastEditedDocumentPartidaIndex === null && // null means declaration-level
    lastEditedDocumentDocumentIndex !== null &&
    declaration.documentos && 
    declaration.documentos.length > 1; // More than one documento to apply to

  const handleDocumentChange = (
    declarationIndex,
    docIndex,
    field,
    value
  ) => {
    // Validate inputs
    if (declarationIndex === undefined || docIndex === undefined || field === undefined) {
      console.warn('handleDocumentChange: Missing required parameters');
      return;
    }
    
    // Validate editableData
    if (!editableData || !Array.isArray(editableData) || editableData.length === 0) {
      console.warn('handleDocumentChange: editableData is invalid or empty');
      return;
    }
    
    // Validate declarationIndex bounds
    if (declarationIndex < 0 || declarationIndex >= editableData.length) {
      console.warn(`handleDocumentChange: declarationIndex ${declarationIndex} out of bounds`);
      return;
    }
    
    const declaration = editableData[declarationIndex];
    if (!declaration || typeof declaration !== 'object') {
      console.warn(`handleDocumentChange: Invalid declaration at index ${declarationIndex}`);
      return;
    }
    
    // Validate documentos array
    if (!declaration.documentos || !Array.isArray(declaration.documentos)) {
      console.warn(`handleDocumentChange: No documentos array in declaration ${declarationIndex}`);
      return;
    }
    
    // Validate docIndex bounds
    if (docIndex < 0 || docIndex >= declaration.documentos.length) {
      console.warn(`handleDocumentChange: docIndex ${docIndex} out of bounds`);
      return;
    }
    
    const documento = declaration.documentos[docIndex];
    if (!documento || typeof documento !== 'object') {
      console.warn(`handleDocumentChange: Invalid documento at index ${docIndex}`);
      return;
    }
    
    try {
      // Deep clone to avoid mutating original data
      const updatedData = JSON.parse(JSON.stringify(editableData));
      updatedData[declarationIndex].documentos[docIndex][field] = value;
      setEditableData(updatedData);
      // Don't update extractedData here - only update on save
      setIsDirty(true);
      
      dispatch(setLastEditedDocumentField({
        field,
        value,
        declarationIndex,
        partidaIndex: null, // null for declaration-level
        documentIndex: docIndex,
      }));
    } catch (error) {
      console.error('handleDocumentChange: Error updating document:', error);
    }
  };

  // Handle "Apply to All" for declaration-level documentos
  const handleApplyToAllDeclarationDocuments = () => {
    if (!canShowApplyButton || lastEditedDocumentDocumentIndex === null) return;
    
    const sourceDoc = declaration.documentos[lastEditedDocumentDocumentIndex];
    if (!sourceDoc || !lastEditedDocumentField) return;
    
    const updatedData = JSON.parse(JSON.stringify(editableData));
    const field = lastEditedDocumentField;
    const value = sourceDoc[field];
    
    // Apply the edited field value to all other documentos in the declaration
    updatedData[declarationIndex].documentos.forEach((doc, docIndex) => {
      if (docIndex !== lastEditedDocumentDocumentIndex) {
        doc[field] = value;
      }
    });
    
    setEditableData(updatedData);
    setIsDirty(true);
    dispatch(clearLastEditedDocumentField());
  };

  return (
    <div className="declaration-documents-table-content">
      {/* Apply to All Button */}
      {canShowApplyButton && (
        <div className="mb-4 flex justify-end">
          <button
            className="py-2 text-xs flex items-center gap-2 rounded-full px-3 bg-primary disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleApplyToAllDeclarationDocuments}
            title={getDeclarationSummaryTranslation('saveAllDocumentsTooltip', isSpanish)}
            type="button"
          >
            <Save className="size-4" />
            <span className="text-xs">{getDeclarationSummaryTranslation('saveAllDocuments', isSpanish)}</span>
          </button>
        </div>
      )}
      <div className="overflow-auto">
        <table className="min-w-full border-collapse text-sm">
        <thead>
          <tr>
            <th className="py-2 px-4 pt-2 pl-0">
              <p className="w-max">#</p>
            </th>
            <th className="py-2 px-4 pt-2">
              <p className="w-max">
                {!isSpanish ? "Document Code" : "Código de Documento"}
              </p>
            </th>
            <th className="py-2 px-4 pt-2">
              <p className="w-max">{!isSpanish ? "Type" : "Tipo"}</p>
            </th>
            <th className="py-2 px-4 pt-2">
              <p className="w-max">{!isSpanish ? "Number" : "Número"}</p>
            </th>
            <th className="py-2 px-4 pt-2">
              <p className="w-max">{!isSpanish ? "Date" : "Fecha"}</p>
            </th>
            <th className="py-2 px-4 pt-2"></th>
          </tr>
        </thead>
        <tbody>
          {declaration.documentos?.map((doc, docIndex) => (
            <tr key={docIndex}>
              <td className="py-4 px-4 pl-0 border-t">
                <span className="text-sm">{docIndex + 1}</span>
              </td>
              <td className="py-4 px-4 border-t">
                <div className="relative w-full">
                  <button
                    type="button"
                    className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${doc.codigo_documento ? '' : 'opacity-75'}`}
                    style={{ maxWidth: '260px' }}
                    onClick={() => setOpenDocModalIndex(`${declarationIndex}_declaration_${docIndex}`)}
                    id={`declaration_documents_codigo_documento_${declarationIndex + 1}_${docIndex}`}
                  >
                    {doc.codigo_documento ? (
                      (() => {
                        const selectedDoc = documentos.find(d => d.codigo === doc.codigo_documento || d.nombre === doc.codigo_documento);
                        return selectedDoc ? (
                          <span
                            className="truncate"
                            style={{ maxWidth: '200px', display: 'inline-block' }}
                            title={`${selectedDoc.codigo} - ${selectedDoc.nombre}`}
                          >
                            {selectedDoc.codigo} {selectedDoc.nombre && <span className="text-xs text-gray-400 ml-2">({selectedDoc.nombre})</span>}
                          </span>
                        ) : (
                          <span className="truncate" title={doc.codigo_documento}>{doc.codigo_documento}</span>
                        );
                      })()
                    ) : (
                      <span className="truncate" title={!isSpanish ? 'Select Document Type' : 'Seleccionar Tipo de Documento'}>{!isSpanish ? 'Select Document Type' : 'Seleccionar Tipo de Documento'}</span>
                    )}
                    <ChevronsUpDown className="w-4 h-4" />
                  </button>
                  <DocumentosModal
                    documentos={documentos}
                    isOpen={openDocModalIndex === `${declarationIndex}_declaration_${docIndex}`}
                    onClose={() => setOpenDocModalIndex(null)}
                    onSelect={(docSelected) => {
                      handleDocumentChange(
                        declarationIndex,
                        docIndex,
                        'codigo_documento',
                        docSelected.codigo
                      );
                    }}
                    selectedDocumento={documentos.find(d => d.codigo === doc.codigo_documento || d.nombre === doc.codigo_documento)}
                    title={!isSpanish ? 'Select Document Type' : 'Seleccionar Tipo de Documento'}
                  />
                </div>
              </td>
              <td className="py-4 px-4 border-t">
                <button
                  type="button"
                  className={`w-full flex gap-2 justify-between items-center custom-dropwdown text-sm py-1 ${doc.tipo ? '' : 'opacity-75'}`}
                  onClick={() => setOpenTipoModal({ [`${declarationIndex}_declaration_${docIndex}`]: true })}
                  id={`declaration_documents_tipo_${declarationIndex + 1}_${docIndex}`}
                >
                  {(() => {
                    const tipos = getDocumentosTipos();
                    const selectedTipo = tipos.find(t => t.codigo === String(doc.tipo));
                    if (selectedTipo) {
                      return <span className="truncate">{selectedTipo.codigo} <span className="text-xs text-gray-400 ml-2">({selectedTipo.nombre})</span></span>;
                    } else if (doc.tipo) {
                      return <span className="truncate" title={doc.tipo}>{doc.tipo}</span>;
                    } else {
                      return <span className="truncate">{!isSpanish ? 'Select Type' : 'Seleccionar Tipo'}</span>;
                    }
                  })()}
                  <ChevronsUpDown className="w-4 h-4" />
                </button>
                <DocumentosTipoModal
                  tipos={getDocumentosTipos()}
                  isOpen={!!openTipoModal[`${declarationIndex}_declaration_${docIndex}`]}
                  onClose={() => setOpenTipoModal({ ...openTipoModal, [`${declarationIndex}_declaration_${docIndex}`]: false })}
                  onSelect={tipo => handleDocumentChange(
                    declarationIndex,
                    docIndex,
                    'tipo',
                    Number(tipo.codigo)
                  )}
                  selectedTipo={getDocumentosTipos().find(t => t.codigo === String(doc.tipo))}
                  title={!isSpanish ? 'Select Type' : 'Seleccionar Tipo'}
                />
              </td>
              <td className="py-4 px-4 border-t">
                <InputWithCopy
                  id={`declaration_documents_numero_${declarationIndex + 1}_${docIndex}`}
                  value={doc.numero}
                  placeholder={`${
                    !doc.numero && !isSpanish
                      ? "Enter Number"
                      : "Entrada Número"
                  }`}
                  onChange={(e) =>
                    handleDocumentChange(
                      declarationIndex,
                      docIndex,
                      "numero",
                      e.target.value
                    )
                  }
                  className="w-full border rounded px-2 py-1 pr-8"
                />
              </td>
              <td className="py-4 px-4 border-t">
                <InputWithCopy
                  id={`declaration_documents_fecha_${declarationIndex + 1}_${docIndex}`}
                  value={doc.fecha}
                  placeholder={`${
                    !doc.fecha && !isSpanish ? "Enter Date" : "Entrada Fecha"
                  }`}
                  onChange={(e) =>
                    handleDocumentChange(
                      declarationIndex,
                      docIndex,
                      "fecha",
                      e.target.value
                    )
                  }
                  className="w-full border rounded px-2 py-1 pr-8"
                />
              </td>
              <td className="py-4 px-4 border-t">
                <button
                  className="py-1 text-xs flex items-center gap-1 text-red-500"
                  onClick={() => {
                    // Deep clone to avoid mutating original data
                    const updatedData = JSON.parse(JSON.stringify(editableData));
                    updatedData[declarationIndex].documentos.splice(docIndex, 1);
                    setEditableData(updatedData);
                    // Don't update extractedData here - only update on save
                    setIsDirty(true);
                    dispatch(setLastEditedDocumentField({
                      field: '__declaration_documents__',
                      value: null,
                      declarationIndex,
                      partidaIndex: null,
                      documentIndex: docIndex,
                    }));
                  }}
                >
                  <Trash className={`size-4 stroke-red-500`} />
                  {isSpanish ? "Eliminar" : "Delete"}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
};

